<template>
    <div class="">
        {{ ACTIVE_PLANS }}
        <Hero title="Support Service Terms" colour-index="0" subtitle="Last update: November 13, 2024" :left="true" />

        <div class="support-terms px-3 sm:px-28 sm:mt-12 sm:pb-12">
            <h5 class="mb-5 mt-5">Introduction</h5>
            <p class="mb-2.5">
                Unified API Inc. ("Unified," "Unified.to," "we," "us," or "our") provides support for our platform
                ("Services") to customers ("Customers," "you") according to the chosen Unified.to plan. These Support
                Services Terms ("Support Terms") outline the available support with your plan and are governed by our
                Terms of Service.
            </p>
            <p class="mb-2.5">
                Our Support Services are designed to help you get the most from your Unified.to investment. Whether
                you're on the Start, Grow, Scale, or a custom Enterprise plan, we're here to provide the support your
                team needs.
            </p>
            <p class="mb-2.5">
                Explore the outline below to see how each plan tier offers specific benefits, response times, and
                communication channels to keep your experience seamless and productive.
            </p>

            <h5 class="mb-5 mt-5">Support Levels by Plan</h5>
            <p class="mb-0">
                Unified.to provides a range of support tiers across four key service plans: Test, Start, Grow, and
                Scale. Each plan is tailored to meet specific business needs, from early-stage testing to high-scale
                production setups.
            </p>

            <ComparisonTable :sections="SUPPORT_FEATURES" :columns="ACTIVE_PLANS" hide-price hide-cta />

            <div class="w-full my-12"></div>

            <h5 class="mb-5 mt-5">Support Channels</h5>
            <p class="mb-2.5">
                We offer multiple support channels tailored to your plan level, ensuring responsive communication and
                technical support:
            </p>
            <ul class="pl-10 mt-5 list-disc">
                <li class="mb-2.5"><b>Public Discord Channel (Test):</b> Join our community on Discord for general
                    support and
                    questions.</li>
                <li class="mb-2.5"><b>Private Slack/Discord (Start, Grow, Scale):</b> Customers on the Start, Grow, and
                    Scale plans
                    gain access to a private support channel via Slack or Discord for direct, prioritized assistance
                    from our support team.</li>
            </ul>

            <h5 class="mb-5 mt-5">Response Times</h5>
            <p class="mb-2.5">
                Our commitment to timely support is outlined below. We aim to respond to all inquiries during business
                hours
                (9 am - 5 am ET), with faster response times available at higher plan tiers.
            </p>

            <h6 class="mt-6 mb-2">Business Hours Expected Response Time:</h6>
            <ul class="pl-10 mt-5 list-disc">
                <li class="mb-2.5"><b>Test:</b> Within 24 hours</li>
                <li class="mb-2.5"><b>Start:</b> Within 12 hours</li>
                <li class="mb-2.5"><b>Grow:</b> Within 2 hours</li>
                <li class="mb-2.5"><b>Scale:</b> Within 1 hour</li>
            </ul>

            <h6 class="mt-6 mb-2">Outside Business Hours Expected Response Time:</h6>
            <ul class="pl-10 mt-5 list-disc">
                <li class="mb-2.5"><b>Scale:</b> Within 12 hours for urgent requests</li>
                <li class="mb-2.5"><b>Other Plans:</b> Support is available during standard business hours, with the
                    exception of
                    critical
                    system issues.</li>
            </ul>

            <h5 class="mb-5 mt-5">New Integration Requests</h5>
            <p class="mb-2.5">
                We are committed to evolving our platform based on user needs. For each support plan:
            </p>
            <ul class="pl-10 mt-5 list-disc">
                <li class="mb-2.5"><b>Test and Start:</b> Your requests will be added to our roadmap and considered for
                    future
                    development.</li>
                <li class="mb-2.5"><b>Grow and Scale:</b> New integration requests are prioritized, offering faster
                    implementation
                    timelines
                    and greater responsiveness to strategic needs.</li>
            </ul>

            <h5 class="mb-5 mt-5">Account Reviews and Project Meetings</h5>
            <p class="mb-2.5">
                Higher-tier plan customers receive dedicated support beyond standard assistance:
            </p>
            <ul class="pl-10 mt-5 list-disc">
                <li class="mb-2.5"><b>Quarterly Post-Launch Account Review (Scale):</b> Regular check-ins every quarter
                    to review
                    integration
                    performance, gather feedback, and discuss strategic opportunities for improvement.</li>
                <li class="mb-2.5"><b>Pre-Launch Weekly Project Meetings (Grow, Scale):</b> Weekly meetings for
                    planning, progress
                    updates,
                    and support to ensure smooth integration and implementation.</li>
            </ul>

            <p class="mt-8 text-sm italic">
                Note: Support terms, including response times and available services, are subject to change based on
                Unified.to's
                support policies. We are committed to transparency and will notify customers of any updates in our
                terms. For
                questions or additional support inquiries, please contact us.
            </p>
        </div>
    </div>
</template>

<script lang="ts">
import { setTitle } from '~~/services/common';
import { SUPPORT_FEATURES } from '~/constants/support';
import { PLANS } from '~/models/UnifiedInternal';

export default {
    name: 'SupportTerms',

    setup() {
        setTitle(
            'Support Service Terms',
            'Unified.to support service terms covering response times, support channels, and plan entitlements.'
        );
    },

    data() {
        return {
            SUPPORT_FEATURES
        }
    },

    computed: {
        ACTIVE_PLANS() {
            return PLANS.filter(plan => plan.is_active).sort((a, b) => (a.min_price ?? 0) - (b.min_price ?? 0)).map(p => ({ name: p.name, key: p.name }));
            ;
        }
    },
}
</script>

<style scoped>
h5 {
    margin-bottom: 20px;
    margin-top: 20px;
}

p {
    color: var(--medium-gray);
}

ul {
    padding-left: 40px;
    margin-top: 20px;
    list-style: disc;
    color: var(--medium-gray);
}

ul li {
    margin-bottom: 10px;
}

a {
    text-decoration: underline;
}
</style>
